/**
 * Che cosa è il socio, per come consuma: una famiglia, un ufficio, un negozio,
 * un'impresa, una fabbrica, un ente pubblico, un'associazione. Non è la sua
 * forma giuridica — quella non serve al profilo energetico.
 *
 * `residential` è l'unico tipo domestico (profilo GSE «domestico»), tutti gli
 * altri sono «altri usi». I codici restano in inglese perché li usano anche le
 * funzioni SQL; le etichette sono in {@link MEMBER_TYPE_LABELS}.
 *
 * `religious`, `research` e `cooperative` sono ritirati (20261028): esistono
 * ancora nell'enum del database, ma nessun socio li ha più.
 */

import type { MemberAppliance } from '../energy/appliances';
import type { EnergyProfileGap } from '../energy/energy-profile';
export type MemberType =
	| 'residential'
	| 'office'
	| 'shop'
	| 'sme'
	| 'factory'
	| 'public_administration'
	| 'ets';

export type ConsumptionProfileCode =
	| 'p1_residential'
	| 'p2a_office'
	| 'p2b_retail'
	| 'p2c_food'
	| 'p3_industrial_2t'
	| 'p4_industrial_h24';

export interface ConsumptionProfileData {
	// ── Obbligatorie comuni ────────────────────────────────────────────────────
	consumoAnnuoKwh?: string;
	// ── Obbligatorie P1 ───────────────────────────────────────────────────────
	rientroPranzo?: 'si' | 'no';
	tipoAC?: 'split' | 'pompa_calore' | 'nessuno';
	// ── Obbligatorie P2a/P2b/P2c ─────────────────────────────────────────────
	orarioApertura?: string;
	orarioChiusura?: string;
	// ── Obbligatorie P2a ──────────────────────────────────────────────────────
	hasServerRoom?: 'si' | 'no';
	tipoClimatizzazione?: 'split' | 'hvac_centralizzato' | 'nessuno';
	// ── Obbligatorie P2b ──────────────────────────────────────────────────────
	aperturaWeekend?: 'si' | 'no';
	aperturaDomenica?: 'aperto' | 'chiuso' | 'ridotto';
	// ── Obbligatorie P2c ──────────────────────────────────────────────────────
	sottotipoRistorazione?: 'bar' | 'ristorante' | 'alimentari' | 'supermercato';
	hasBanchiFrigo?: 'si' | 'no';
	// ── Obbligatorie P3 ───────────────────────────────────────────────────────
	numTurni?: string;
	hasCarichiNotturni?: 'si' | 'no';
	orarioPausa?: string;
	// ── Obbligatorie P4 ───────────────────────────────────────────────────────
	produzioneH24?: 'si' | 'no';
	intensitaTurnoNotturno?: 'piena' | 'ridotta';
	// ── Obbligatorie P3+P4 ────────────────────────────────────────────────────
	potenzaContrattualeKw?: string;
	// ── Opzionali P1 ──────────────────────────────────────────────────────────
	numPersone?: string;
	oreACEstive?: string;
	riscaldamentoElettrico?: 'si' | 'no';
	veicoloElettrico?: 'si' | 'no';
	presenzaGiornoFeriali?: 'si' | 'no';
	// ── Opzionali P2a ─────────────────────────────────────────────────────────
	aperturaWeekendUfficio?: 'si' | 'no';
	orarioWeekendUfficio?: string;
	numPostazioniLavoro?: string;
	riscaldamentoElettricoUfficio?: 'elettrico' | 'gas' | 'teleriscaldamento';
	illuminazioneUfficio?: 'led' | 'fluorescente' | 'mista';
	// ── Opzionali P2b ─────────────────────────────────────────────────────────
	tipoClimatizzazioneRetail?: 'split' | 'hvac_centralizzato' | 'nessuno';
	superficieVendita?: string;
	illuminazioneRetail?: 'led' | 'fluorescente' | 'alogeni';
	chiusuraStagionale?: 'si' | 'no';
	// ── Opzionali P2c ─────────────────────────────────────────────────────────
	aperturaFestivi?: 'si' | 'no';
	cucinaProf?: 'si' | 'no';
	tipoClimatizzazioneRisto?: 'split' | 'hvac_centralizzato' | 'nessuno';
	superficieLocaleCoperti?: string;
	// ── Opzionali P3 ──────────────────────────────────────────────────────────
	sabatoLavorativo?: 'si_pieno' | 'si_ridotto' | 'no';
	tipoAvvioMacchinari?: 'diretto' | 'inverter';
	settoreAtecoIndustriale?: string;
	// ── Opzionali P4 ──────────────────────────────────────────────────────────
	fermataAnnualeSettimane?: string;
	tipoProcesso?: 'termico' | 'meccanico' | 'informatico' | 'frigorifero';
	hasCogenerazione?: 'si' | 'no';
}

export type MemberRole = 'consumer' | 'producer' | 'prosumer';
export type MemberStatus = 'verified' | 'incomplete' | 'pending' | 'takeover' | 'withdrawn';

/** Dati necessari alle viste energetiche, senza anagrafica o documenti del socio. */
export type EnergyMember = Pick<
	Member,
	'id' | 'nome' | 'tipo' | 'ruolo' | 'stato' | 'consumptionProfile' | 'consumptionProfileData'
> & {
	/**
	 * Visto da un altro socio senza il suo consenso (INB-357): `nome` è vuoto e
	 * le classifiche lo mostrano come socio anonimo. Tipo e ruolo restano.
	 */
	anonimo?: boolean;
	/** È la riga di chi sta guardando: nelle classifiche la si riconosce come «tu». */
	sonoIo?: boolean;
};

/** Stato dell'accesso al portale di un socio (INB-396). */
export type MemberAccess = 'attivo' | 'invitato' | 'nessuno';

export interface Member {
	id: string;
	tenantId: string;
	nome: string;
	tipo: MemberType;
	ruolo: MemberRole;
	stato: MemberStatus;
	/** I dispositivi elettrici al prelievo (vedi `MemberAppliance`). */
	dispositivi?: MemberAppliance[];
	/** Giorno di approvazione del recesso (`YYYY-MM-DD`); vedi {@link isMemberInMonth}. */
	dataRecesso?: string | null;
	/** Per l'invito e l'accesso al portale. */
	email?: string | null;
	dataIscrizione?: string | null;
	invitedAt?: string | null;
	userId?: string | null;
	/**
	 * Accesso al portale (INB-396), solo nella lista soci dell'admin:
	 * `attivo` è entrato almeno una volta, `invitato` ha un account mai usato,
	 * `nessuno` non ha un account. Assente se non è stato possibile leggerlo.
	 */
	accesso?: MemberAccess;
	/**
	 * Cosa manca al profilo energetico (`energyProfileGaps` in
	 * @slims/shared/energy), solo nella lista soci dell'admin. Vuoto = completo,
	 * ed è da qui che viene `stato`. Assente se non è stato possibile leggerlo.
	 */
	profiloMancanti?: EnergyProfileGap[];
	/** Consenso a comparire per nome nelle classifiche viste dagli altri soci (INB-357). */
	classificaConsenso?: boolean;
	// ── Profilo di consumo ────────────────────────────────────────────────────
	/**
	 * Il socio ha un sistema di accumulo.
	 *
	 * Non è un ruolo ma decide un punto di misura come se lo fosse: insieme a
	 * `ruolo` determina quali punti devono esistere (`metersForRole`), e sposta
	 * il profilo GSE del prelievo su `PACM`. La colonna esisteva già e la
	 * pipeline dei profili tipici la leggeva; mancava solo qui.
	 */
	hasStorage?: boolean | null;
	/** Codice profilo SLP adattato — vedi ConsumptionProfileCode */
	consumptionProfile?: ConsumptionProfileCode | string | null;
	/** Risposte alle domande obbligatorie/opzionali del profilo selezionato */
	consumptionProfileData?: ConsumptionProfileData | null;
	createdAt?: string | null;
	updatedAt?: string | null;
}

export const MEMBER_TYPE_LABELS: Record<MemberType, string> = {
	residential: 'Famiglia',
	office: 'Ufficio',
	shop: 'Negozio',
	sme: 'Impresa',
	factory: 'Fabbrica',
	public_administration: 'Ente pubblico',
	ets: 'Associazione',
};

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
	consumer: 'Consumatore',
	producer: 'Produttore',
	prosumer: 'Prosumer',
};

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
	verified: 'Completo',
	incomplete: 'Incompleto',
	pending: 'In Attesa',
	takeover: 'Subentro',
	withdrawn: 'Receduto',
};

/**
 * Il socio fa parte della configurazione nel mese (`month` 1–12)? Chi ha
 * receduto conta fino alla fine del mese del recesso ed esce dal primo giorno
 * del successivo, come nel calcolo salvato e in `energy_shared` (INB-373). Un
 * receduto senza data non conta mai.
 */
export function isMemberInMonth(
	member: { stato: string; dataRecesso?: string | null },
	year: number,
	month: number,
): boolean {
	if (member.stato !== 'withdrawn') return true;
	if (!member.dataRecesso) return false;
	return member.dataRecesso.slice(0, 7) >= `${year}-${String(month).padStart(2, '0')}`;
}
